import {
  urlForConversationWithID,
  urlForConversationWithServerID,
  useConversationIdFromSearchParams,
  useUrlNormalization,
} from "@mirage/mosaics/Chat/utils/navigation";
import { useWorkflowAgentDetails } from "@mirage/mosaics/ComposeAssistant/components/workflow-agent/useWorkflowAgentDetails";
import { useStackChatDetails } from "@mirage/mosaics/ConversationAssistant/components/stack-chat/useStackChatDetails";
import { useChatRoute } from "@mirage/mosaics/ConversationAssistant/hooks/useChatRoute";
import { useWorkflowAgentDeps } from "@mirage/mosaics/ConversationAssistant/hooks/useWorkflowAgentDeps";
import { createNewConversation } from "@mirage/service-conversation";
import useConversation from "@mirage/service-conversation/hooks/useConversation";
import { useWorkflowAgent } from "@mirage/service-conversation/hooks/useWorkflowAgents";
import {
  ConversationType,
  type ConversationRouterState,
} from "@mirage/service-conversation/router_state_types";
import type {
  Conversation,
  ConversationClientContext,
  WorkflowAgent,
} from "@mirage/service-conversation/types";
import { convertUUIDsToChatSource } from "@mirage/service-conversation/utils/convertDocumentInfoToChatSource";
import { tagged } from "@mirage/service-logging";
import { usePrevious } from "@mirage/shared/util/hooks";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { useCallback, useEffect, useState } from "react";
import type { Location } from "react-router-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  useManuallyAddedSources,
  useScopedSources,
} from "../../hooks/attachments/useManuallyAddedSources";
import { useFssConversationDetails } from "../../hooks/useFssConversationDetails";

const logger = tagged("[chat][fss] useRouteHandling");

/**
 * Hook to handle listening to routing changes and updating the conversation state; as well as
 * logic for navigating away from the current conversation.
 */
export function useRouteHandling(
  variant: "default" | "condensed" | "workflow-agent-preview",
  onConversationIdChange?: (conversationId: string | undefined) => void,
  previewWorkflowAgent?: WorkflowAgent | undefined,
  shouldUseRouterNavigate?: boolean,
) {
  // Fix URL normalization issue on staging.dash.ai
  useUrlNormalization();

  const chatRoute = useChatRoute();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();

  const { conversationId, resetNewConversation, isConversationLoading } =
    useConversationId(searchParams);
  const conversation = useConversation(conversationId);

  useEffect(() => {
    onConversationIdChange?.(conversationId);
  }, [conversationId, onConversationIdChange]);

  const stackChatDetails = useStackChatDetails(state, conversation);
  const workflowAgentConfig = useWorkflowAgentConfig(
    state,
    conversation,
    variant === "workflow-agent-preview",
    previewWorkflowAgent,
  );
  const fssConversationDetails = useFssConversationDetails(state, conversation);

  // Update URL when conversation gets a server ID assigned
  const prevServerConversationId = usePrevious(
    conversation?.serverConversationId,
  );
  useEffect(() => {
    if (
      conversation?.serverConversationId &&
      !prevServerConversationId &&
      !searchParams.get("serverConversationId") &&
      variant !== "workflow-agent-preview"
    ) {
      const newUrl = urlForConversationWithServerID(
        conversation.serverConversationId,
        chatRoute,
      );
      if (shouldUseRouterNavigate) {
        navigate(newUrl, { state });
      } else {
        // Update URL without triggering a re-render
        window.history.replaceState(state, "", newUrl);
      }
    }
  }, [
    conversation?.serverConversationId,
    prevServerConversationId,
    conversationId,
    searchParams,
    chatRoute,
    state,
    variant,
    navigate,
    shouldUseRouterNavigate,
  ]);
  const scenarioMeta = state?.scenarioMeta;

  const navigateNewConversation = useCallback(
    async (routerState?: ConversationRouterState) => {
      const hasNavigated = await resetNewConversation(routerState);

      // Only if we're trying to initialize a new, empty, conversation,
      // then we need to explicitly navigate to the chat page here.
      // Otherwise, the only navigation occuring in `resetNewConversation` is
      // based on either an existing conversation id or a new, predefined
      // conversation state.
      if (!hasNavigated) {
        navigate(chatRoute, { replace: true });
      }
    },
    [resetNewConversation, chatRoute, navigate],
  );

  const navigateNewWorkflowAgent = useCallback(() => {
    navigate(RoutePath.NEW_WORKFLOW_AGENT);
  }, [navigate]);

  return {
    conversationId,
    conversation,
    conversationType:
      state?.conversationType ||
      // If the router state doesn't have a conversation type, use the client context
      // to determine the conversation type.
      // NOTE: This isn't strictly necessary anymore, but it feels better than
      // just returning undefined for a conversation loaded from history.
      calculateConversationTypeFromClientContext(conversation?.clientContext),
    isConversationLoading,
    stackChatDetails,
    workflowAgentConfig,
    fssConversationDetails,
    scenarioMeta,
    navigateNewConversation,
    navigateNewWorkflowAgent,
  };
}

/**
 * Hook to handle tracking workflow agent state against the current route
 */
export function useWorkflowAgentConfig(
  state: Location["state"],
  conversation: Conversation | undefined,
  showPreview: boolean,
  previewWorkflowAgent: WorkflowAgent | undefined,
) {
  const { setScopedSources } = useScopedSources();

  const workflowAgentDetails = useWorkflowAgentDetails(state, conversation);
  const workflowAgentId = showPreview
    ? "preview"
    : workflowAgentDetails?.agentId;
  const workflowAgentFromHook = useWorkflowAgent(workflowAgentId);
  const workflowAgent =
    workflowAgentId === "preview"
      ? previewWorkflowAgent
      : workflowAgentFromHook;
  const workflowAgentFields = useWorkflowAgentDeps(workflowAgent);

  useEffect(() => {
    if (workflowAgent) {
      setScopedSources(
        workflowAgent.accessType === "private"
          ? convertUUIDsToChatSource(
              workflowAgent.sources,
              "dash_search_result",
              "scope",
            )
          : workflowAgent.sourceContents || [],
      );
    }
  }, [workflowAgent, setScopedSources]);

  return {
    workflowAgent,
    workflowAgentFields,
    model: workflowAgentDetails?.model,
  };
}

/**
 * Hook to handle tracking conversation state against the current route
 */
function useConversationId(searchParams: URLSearchParams) {
  const navigate = useNavigate();
  const chatRoute = useChatRoute();
  const { setManuallyAddedSources } = useManuallyAddedSources();

  const [conversationId, setConversationId] = useState<string | undefined>(
    undefined,
  );
  const conversationIdLookupFromSearchParams =
    useConversationIdFromSearchParams(searchParams);

  /**
   * Creates a new conversation and navigates to it if the router state is provided.
   *
   * @param routerState - The router state to navigate to.
   * @returns True if the conversation was created and navigated to, false otherwise.
   */
  const resetToNewConversation = useCallback(
    async (routerState?: ConversationRouterState): Promise<boolean> => {
      logger.info("Calling resetNewConversation, state: ", {
        ...routerState,
        message:
          routerState && "message" in routerState ? "[redacted]" : undefined,
      });
      return createNewConversation()
        .then((conversation) => {
          setConversationId(conversation.id);
          setManuallyAddedSources([]);

          // If a new conversation was created as the result of a stateful navigation, update
          // the URL to point to the newly created conversation ID, replacing the current entry
          // and persisting the router state.
          let hasNavigated = false;
          if (routerState && "conversationType" in routerState) {
            navigate(urlForConversationWithID(conversation.id, chatRoute), {
              replace: true,
              state: routerState,
            });
            hasNavigated = true;
          }

          return hasNavigated;
        })
        .catch((e) => {
          throw e;
        });
    },
    [setManuallyAddedSources, navigate, chatRoute],
  );

  useEffect(() => {
    if (conversationIdLookupFromSearchParams.state === "loaded") {
      if (conversationIdLookupFromSearchParams.conversationId) {
        setConversationId(conversationIdLookupFromSearchParams.conversationId);
      } else {
        // If there is no conversation id in the search params only if we don't already
        // have a conversation id from another source. This prevents duplicate resets after
        // an explicit call to resetNewConversation.
        if (!conversationId) {
          logger.info(
            "Resetting new conversation based on lack of conversationId in search params",
          );
          logger.info("conversationIdLookupFromSearchParams", {
            conversationIdLookupFromSearchParams,
          });
          resetToNewConversation();
        }
      }
    }
  }, [conversationIdLookupFromSearchParams, resetToNewConversation]);
  return {
    conversationId,
    resetNewConversation: resetToNewConversation,
    isConversationLoading:
      conversationIdLookupFromSearchParams.state === "loading",
  };
}

export const calculateConversationTypeFromClientContext = (
  clientContext?: ConversationClientContext,
) => {
  switch (clientContext?.clientScenario) {
    // FSS contexts
    case "fss-unscoped":
      return ConversationType.FssAsk;
    case "fss-serp-answer":
      return ConversationType.FssAskQuery;
    case "fss-serp-follow-up":
      return ConversationType.FssAskQuery;
    case "fss-suggested-query":
      return ConversationType.FssAskFromSuggestion;
    case "fss-file-preview":
      return ConversationType.FssAskWithFile;
    case "fss-stack":
      return ConversationType.FssAskWithFile;
    case "fss-folder":
      return ConversationType.FssAskWithFolder;

    // Non-FSS contexts
    case "suggested_query":
      return ConversationType.AskFromSuggestion;
    case "stack":
      return ConversationType.AskWithMultiDocs; // or AskWithPromptMultiDocs depending on whether there's a message

    default:
      return undefined;
  }
};
